import React, { useState, useEffect, useRef } from 'react';
import { MAP_STYLE } from './constants/index';
import { ExpiredMapView } from '../ExpiredMapView';
import { MapStatusTile } from '../MapStatusTile';
import { InitialMapView } from '../InitialMapView';
import { EmptyMapView } from '../EmptyMapView';
import Shimmer from 'sushiweb/Shimmer';
import { useSelector } from 'react-redux';
import { InitialMapViewV1 } from '../InitialMapViewV1';
import { createHomeLabelOverlay, getImage } from './utils';

const MAP_EXPIRY_TIME = 60 * 1000;

export function RenderMap({
  mapData,
  refreshMap,
  initialLoad,
  orderBasicInfo = null,
  orderId,
  userId,
}) {
  const [isMapViewExpired, setIsMapViewExpired] = useState(false);
  const isNewUI = useSelector((state) => state.ordersState?.isNewUI);

  useEffect(() => {
    let id = setTimeout(() => {
      setIsMapViewExpired(true);
    }, MAP_EXPIRY_TIME);

    return () => {
      clearTimeout(id);
    };
  }, []);

  if (!orderBasicInfo) return <Shimmer height='20rem' width='100%' />;

  const bounds = mapData?.location_markers.map((ll) => ll.lat_lng);

  if (initialLoad) {
    if (isNewUI) {
      return (
        <InitialMapViewV1
          refreshMap={refreshMap}
          orderId={orderId}
          userId={userId}
        />
      );
    } else {
      return <InitialMapView refreshMap={refreshMap} />;
    }
  }

  if (!mapData) return null;

  if (isMapViewExpired) {
    return <ExpiredMapView refreshMap={refreshMap} />;
  }

  if (!mapData.polylines || mapData.polylines.length === 0) {
    return <EmptyMapView />;
  }

  return (
    <>
      <Map boundsProp={bounds}>
        {/* Store and Customer Location Markers */}
        {mapData.location_markers.map((marker, i) => {
          return (
            <Marker
              key={i}
              {...marker}
              NDPODetails={
                orderBasicInfo?.order?.no_delivery_pin_address_details || {}
              }
            />
          );
        })}

        {/* Green road lines */}
        {mapData.polylines?.map((polyline, i) => {
          return <Polyline key={i} is_dashed={true} {...polyline} />;
        })}

        {/* Rider Location Marker */}
        {mapData?.rider_details?.lat_lng && mapData?.rider_details?.image ? (
          <Marker
            lat_lng={mapData.rider_details?.lat_lng}
            image={mapData.rider_details?.image}
          />
        ) : null}
      </Map>

      {mapData.rider_details?.last_location_update_ts ? (
        <MapStatusTile
          lastUpdatedAt={mapData.rider_details.last_location_update_ts}
        />
      ) : null}
    </>
  );
}

export function OrderRunMap({
  orderRunInfo,
  refreshMap,
  initialLoad,
  orderBasicInfo = null,
  orderId,
  userId,
}) {
  const isNewUI = useSelector((state) => state.ordersState?.isNewUI);
  if (!orderBasicInfo) return <Shimmer height='20rem' width='100%' />;

  if (initialLoad) {
    if (isNewUI) {
      return (
        <InitialMapViewV1
          refreshMap={refreshMap}
          orderId={orderId}
          userId={userId}
        />
      );
    } else {
      return <InitialMapView refreshMap={refreshMap} />;
    }
  }

  if (
    !orderRunInfo ||
    !('location_markers' in orderRunInfo) ||
    !orderRunInfo['location_markers'].length
  )
    return <EmptyMapView />;

  let lat_lngs = [];
  let markers = [];
  for (var i = 0; i < orderRunInfo['location_markers'].length; ++i) {
    const marker = orderRunInfo['location_markers'][i];
    lat_lngs.push(marker['lat_lng']);
    markers.push(
      <Marker
        key={i}
        lat_lng={marker['lat_lng']}
        image={'image' in marker ? marker['image'] : null}
        caption={marker['caption']}
        color={'color' in marker ? marker['color'] : null}
        draw_circle={i === orderRunInfo['location_markers'].length - 1}
        NDPODetails={
          orderBasicInfo?.order?.no_delivery_pin_address_details || {}
        }
      />,
    );
  }

  return (
    <Map boundsProp={lat_lngs} data-dd-privacy='hidden'>
      {markers}
      <Polyline is_solid={true} width={10} color='#ffde17' lat_lng={lat_lngs} />
    </Map>
  );
}

function Map({ center, children, boundsProp }) {
  const ref = useRef(null);
  const [map, setMap] = useState();
  const [bounds, setBounds] = useState();

  useEffect(() => {
    if (ref.current && !map) {
      const mapRef = new window.google.maps.Map(ref.current, {
        center,
        zoom: 14,
        disableDefaultUI: true,
        fullscreenControl: true,
        gestureHandling: 'greedy',
        styles: MAP_STYLE,
        keyboardShortcuts: false,
      });

      setMap(mapRef);
    }

    if (ref.current && !bounds) {
      setBounds(new window.google.maps.LatLngBounds());
    }
  }, [ref, map, bounds]);

  useEffect(() => {
    if (bounds && boundsProp) {
      boundsProp.forEach((bnd) => {
        bounds.extend({ lat: bnd.latitude, lng: bnd.longitude });
      });

      map.fitBounds(bounds);
    }
  }, [bounds]);

  return (
    <>
      <div
        style={{
          width: '100%',
          height: '20rem',
        }}
        ref={ref}
        data-dd-privacy='mask'
      />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { map });
        }
      })}
    </>
  );
}

const Marker = ({
  map,
  lat_lng,
  image,
  caption,
  color,
  draw_circle = false,
  NDPODetails = {},
}) => {
  const { show_marker, polygon_type, polygon_details } = NDPODetails || {};

  React.useEffect(() => {
    const options = {
      map: map,
      position: {
        lat: lat_lng?.latitude,
        lng: lat_lng?.longitude,
      },
    };

    if (image) {
      options['icon'] = {
        url: getImage(image.url),
        size: new window.google.maps.Size(
          image.width,
          image.width / image.aspect_ratio,
        ),
        scaledSize: new window.google.maps.Size(
          image.width,
          image.width / image.aspect_ratio,
        ),
      };
    } else if (color) {
      options['icon'] = {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: color,
        fillOpacity: 0.9,
        strokeColor: 'white',
        strokeOpacity: 0.9,
        strokeWeight: 1,
        scale: 4,
      };
    }

    const marker = new window.google.maps.Marker(options);

    let circle = null;
    if (draw_circle) {
      circle = new window.google.maps.Circle({
        radius: 250,
        map: map,
        strokeOpacity: 0,
        visible: false,
      });
      circle.bindTo('center', marker, 'position');
      marker._circle = circle;
      marker.addListener('click', () => {
        marker._circle.setOptions({
          visible: !marker._circle.getVisible(),
        });
      });
    }

    // Draw a visible blue circle for markers whose image url includes "home" and show_marker is true
    let homeCircle = null;

    let homeLabelDiv = null;

    const isHomeMarker =
      image &&
      typeof image.url === 'string' &&
      image.url?.toLowerCase()?.includes('home') &&
      show_marker;

    if (isHomeMarker) {
      homeCircle = new window.google.maps.Circle({
        radius: polygon_details?.radius || 200,
        map: map,
        strokeColor: polygon_details?.color || '#4285F4',
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: polygon_details?.color || '#4285F4',
        fillOpacity: 0.1,
      });
      homeCircle.bindTo('center', marker, 'position');

      // Keep overlay in sync with marker position
      homeLabelDiv = createHomeLabelOverlay(lat_lng, map);

      marker.addListener('position_changed', () => {
        if (homeLabelDiv) {
          homeLabelDiv.position = {
            latitude: marker.getPosition().lat(),
            longitude: marker.getPosition().lng(),
          };
          homeLabelDiv.draw();
        }
      });
    }

    let infoWindow;

    if (caption) {
      infoWindow = new window.google.maps.InfoWindow({
        content: caption,
        disableAutoPan: true,
      });
      marker.addListener('mouseover', () => infoWindow.open(map, marker));
      marker.addListener('mouseout', () => infoWindow.close(map, marker));
    }

    // remove marker from map on unmount
    return () => {
      marker.setMap(null);
      if (infoWindow) {
        infoWindow.setMap(null);
      }
      if (circle) {
        circle.setMap(null);
      }
      if (homeCircle) {
        homeCircle.setMap(null);
      }
    };
  }, [caption, image, lat_lng, map, draw_circle]);

  return null;
};

function Polyline({
  map,
  width,
  color,
  is_encoded,
  geometry,
  lat_lng,
  is_dashed,
  is_solid,
}) {
  const [polyline, setPolyline] = React.useState();

  React.useEffect(() => {
    if (!polyline) {
      setPolyline(new window.google.maps.Polyline());
    }

    // remove marker from map on unmount
    return () => {
      if (polyline) {
        polyline.setMap(null);
      }
    };
  }, [polyline]);

  React.useEffect(() => {
    let options = { map, strokeColor: color };
    if (polyline) {
      if (is_encoded) {
        options = {
          path: window.google.maps.geometry.encoding.decodePath(geometry),
          strokeOpacity: 1.0,
          strokeWeight: width,
          fillOpacity: 0,
          ...options,
        };
      } else if (is_dashed) {
        // doc: https://developers.google.com/maps/documentation/javascript/examples/overlay-symbol-dashed
        options = {
          path: lat_lng.map(
            ({ latitude, longitude }) =>
              new window.google.maps.LatLng({ lat: latitude, lng: longitude }),
          ),
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                scale: 3,
              },
              offset: '0',
              repeat: '15px',
            },
          ],
          strokeOpacity: 0,
          ...options,
        };
      } else if (is_solid) {
        options = {
          path: lat_lng.map(
            ({ latitude, longitude }) =>
              new window.google.maps.LatLng({ lat: latitude, lng: longitude }),
          ),
          geodesic: true,
          strokeOpacity: 1.0,
          strokeWeight: width,
          ...options,
        };
      }

      polyline.setOptions(options);
      polyline.setMap(map);
    }
  }, [polyline]);

  return null;
}
